/**
 * @fileoverview Constantes, rótulos e formatação das ferramentas de Super Admin
 * da conexão (resolver e excluir em massa). Espelha o catálogo do backend
 * (services/AdminToolsServices/adminOperationCatalog.ts).
 */

import { i18n } from "../../translate/i18n";

export const TICKET_STATUSES = ["open", "pending", "closed"];

// Status que cada operação filtra (vazio: não filtra por status). Resolver só
// mexe no que ainda não está resolvido.
const STATUSES_BY_OPERATION = {
	resolve_tickets: ["open", "pending"],
	delete_tickets: TICKET_STATUSES,
	delete_contacts: [],
};
export const statusesOf = (operation) => STATUSES_BY_OPERATION[operation] || [];
export const PERIODS = ["today", "7d", "30d", "custom", "all"];
export const ACTIVE_STATUSES = ["pending", "running", "cancel_requested"];

export const isActiveStatus = (status) => ACTIVE_STATUSES.includes(status);

// Valores vindos de fora (nome de conexão, de usuário) não podem ser lidos
// pelo i18next como marcação: "{{" e "$t(" ganham um espaço de largura zero.
const inert = (value) =>
	typeof value === "string" ? value.replace(/\{\{/g, "{\u200b{").replace(/\$t\(/g, "$\u200bt(") : value;

// Sem escape do i18next: o React já escapa o texto (senão "/" vira "&#x2F;").
export const t = (key, options = {}) => {
	const values = {};
	Object.keys(options).forEach((name) => {
		values[name] = inert(options[name]);
	});
	return i18n.t(`connections.adminTools.${key}`, { ...values, interpolation: { escapeValue: false } });
};

// Frase de confirmação: vem do servidor; esta é só a reserva para um backend
// que ainda não a envie (versões desencontradas durante um deploy).
const FALLBACK_PHRASES = {
	resolve_tickets: "Resolver conversas",
	delete_tickets: "Deletar conversas",
	delete_contacts: "Deletar contatos",
};
export const phraseOf = (op) => op?.phrase || FALLBACK_PHRASES[op?.operation] || "";

// Socket, consulta e respostas chegam fora de ordem: fica a versão mais recente
// (sem updatedAt válido dos dois lados, fica a que chegou por último).
export const newest = (prev, next) => {
	if (!prev || prev.id !== next.id) return next;
	const before = new Date(prev.updatedAt).getTime();
	const after = new Date(next.updatedAt).getTime();
	if (Number.isNaN(before) || Number.isNaN(after)) return next;
	return after >= before ? next : prev;
};

const LOCALES = { en: "en-US", es: "es-ES" };
const locale = () => LOCALES[String(i18n.language || "pt").slice(0, 2)] || "pt-BR";

export const fmtNumber = (value) => new Intl.NumberFormat(locale()).format(Number(value) || 0);

export const fmtDateTime = (value) =>
	value
		? new Date(value).toLocaleString(locale(), {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
				hour: "2-digit",
				minute: "2-digit",
			})
		: "—";

export const fmtTime = (value) =>
	value ? new Date(value).toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";

// "2026-06-10" → data local sem cair no dia anterior por causa do fuso.
export const fmtDay = (day) => (day ? new Date(`${day}T12:00:00`).toLocaleDateString(locale()) : "");

// Os filtros de período do backend são no fuso de São Paulo.
const SAO_PAULO = "America/Sao_Paulo";
const DAY_IN_SAO_PAULO = new Intl.DateTimeFormat("en-CA", { timeZone: SAO_PAULO });
export const todayInSaoPaulo = () => DAY_IN_SAO_PAULO.format(new Date());

// Limites do período no fuso dos filtros — no fuso do navegador, um superadmin
// fora de Brasília veria horas que não batem com os dias escolhidos.
const fmtInSaoPaulo = (value) =>
	new Date(value).toLocaleString(locale(), {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		timeZone: SAO_PAULO,
	});
export const fmtRangeStart = (value) => (value ? fmtInSaoPaulo(value) : "—");

export const periodLabel = (filters) => {
	if (filters?.period !== "custom") return t(`period.${filters?.period || "all"}`);
	if (filters.dateFrom === filters.dateTo) return fmtDay(filters.dateFrom);
	return t("period.customRange", { from: fmtDay(filters.dateFrom), to: fmtDay(filters.dateTo) });
};

// O fim do período é exclusivo (00:00 do dia seguinte, ou o instante da
// prévia): mostra o último instante incluído.
export const fmtRangeEnd = (value) => (value ? fmtInSaoPaulo(new Date(value).getTime() - 1000) : "—");

// Resumo curto dos filtros de uma operação (histórico e progresso).
export const filtersSummary = (operation, filters) => {
	if (!filters) return "";
	const parts = [];
	if (statusesOf(operation).length > 0) {
		parts.push((filters.statuses || []).map((status) => t(`status.${status}`)).join(", "));
	}
	parts.push(periodLabel(filters));
	parts.push(filters.includeGroups ? t("summary.withGroups") : t("summary.withoutGroups"));
	return parts.filter(Boolean).join(" · ");
};

export const errorCodeOf = (err) => err?.response?.data?.error || null;

// Mensagem traduzida do código de erro do backend (mesmo mapa do toastError).
export const messageForCode = (code) =>
	code && i18n.exists(`backendErrors.${code}`) ? i18n.t(`backendErrors.${code}`) : t("errors.generic");

export const errorMessage = (err) => messageForCode(errorCodeOf(err));
